import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, Typography, useToast, Form } from '@crisellevaldez/speculo-ui';
import { Building2 } from 'lucide-react';
import { IconPill } from '../../../common/IconPill';
import { FormattedTextarea } from '../../../common/FormattedTextarea';
import { LoadingDialog } from '../../../common/dialogs/LoadingDialog';
import { useQueryClient } from '@tanstack/react-query';
import { uploadMLSData } from '../../../../api/mls';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { mlsFormSchema, MLSFormData } from '../../../../schemas/mls.schema';
import { useMLSData } from '../../../../hooks/client/mls/useMLSData';

interface MLSFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrgId: string;
  onSuccess: () => void;
  mode: 'create' | 'update';
  boardCode?: string; // Required for update mode
  existingAgentIds?: string[];
  existingOfficeIds?: string[];
}

export const MLSFormModal: React.FC<MLSFormModalProps> = ({
  isOpen,
  onClose,
  selectedOrgId,
  onSuccess,
  mode,
  boardCode = '',
  existingAgentIds = [],
  existingOfficeIds = [],
}) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [agentIds, setAgentIds] = useState<string[]>(mode === 'create' ? [] : existingAgentIds);
  const [officeIds, setOfficeIds] = useState<string[]>(mode === 'create' ? [] : existingOfficeIds);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
  } = useForm<MLSFormData>({
    resolver: zodResolver(mlsFormSchema),
    defaultValues: {
      name: '',
      boardCode: boardCode,
      complianceText: '',
      logo: '',
      agentId: [],
      officeId: [],
    },
  });

  // Use React Query to fetch MLS data
  const {
    data: mlsData,
    isLoading: isFetchingMLS,
    error: mlsError,
  } = useMLSData(mode === 'update' ? boardCode : undefined, mode === 'update' && isOpen);

  // Effect to handle form initialization and data loading
  useEffect(() => {
    if (!isOpen) return;

    // Set loading state when fetching data
    if (mode === 'update' && boardCode && mlsData) {
      setIsLoading(true);
      setLoadingMessage('Fetching MLS data...');

      try {
        if (mlsData.status === 'success' && mlsData.data) {
          setValue('name', mlsData.data.mlsBoardCode); // Using board code as name for now
          setValue('boardCode', mlsData.data.mlsBoardCode);
          setValue('logo', mlsData.data.mlsLogo);
          setValue('complianceText', mlsData.data.complianceText);

          // Set agent IDs and office IDs from props
          setAgentIds(existingAgentIds && existingAgentIds.length > 0 ? [...existingAgentIds] : []);
          setOfficeIds(
            existingOfficeIds && existingOfficeIds.length > 0 ? [...existingOfficeIds] : []
          );
        }
      } catch (error) {
        console.error('Error loading MLS data:', error);
        showToast({
          message: error instanceof Error ? error.message : 'Failed to load MLS data',
          variant: 'error',
        });
      } finally {
        setIsLoading(false);
        setLoadingMessage('');
      }
    } else if (mode === 'create') {
      // Reset form for create mode
      reset({
        name: '',
        boardCode: '',
        complianceText: '',
        logo: '',
        agentId: [],
        officeId: [],
      });
      setAgentIds([]);
      setOfficeIds([]);
    }
  }, [
    isOpen,
    mode,
    boardCode,
    mlsData,
    setValue,
    reset,
    existingAgentIds,
    existingOfficeIds,
    showToast,
  ]);

  const onSubmit = async (data: MLSFormData) => {
    if (!selectedOrgId) return;

    try {
      setIsSubmitting(true);
      setLoadingMessage(
        mode === 'create' ? 'Subscribing to MLS...' : 'Updating MLS subscription...'
      );

      // Include agent IDs, office IDs, and tenant ID in the API call
      const response = await uploadMLSData({
        mlsBoardCode: data.boardCode,
        mlsLogo: data.logo || '',
        complianceText: data.complianceText || '',
        agentId: agentIds,
        officeId: officeIds,
        tenantId: selectedOrgId,
      });

      showToast({
        message: response.message,
        variant: 'success',
      });

      // Invalidate queries
      await queryClient.invalidateQueries({ queryKey: ['MLS'] });
      // Also invalidate the specific tenant query to refresh subscribedMLS and subscribedMLSDetails
      await queryClient.invalidateQueries({ queryKey: ['tenant', selectedOrgId] });
      // Invalidate the specific MLS data query to refresh MLS data from access API
      await queryClient.invalidateQueries({ queryKey: ['mls', 'details', data.boardCode] });

      // Reset and close modal
      handleClose();
      onSuccess();
    } catch (error) {
      console.error(`Error ${mode === 'create' ? 'subscribing to' : 'updating'} MLS:`, error);
      showToast({
        message:
          error instanceof Error
            ? error.message
            : `Failed to ${mode === 'create' ? 'subscribe to' : 'update'} MLS`,
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
      setLoadingMessage('');
    }
  };

  const handleClose = () => {
    reset();
    setAgentIds(existingAgentIds);
    setOfficeIds(existingOfficeIds);
    onClose();
  };

  const modalTitle = mode === 'create' ? 'Subscribe New MLS' : 'Update MLS Subscription';
  const submitButtonText = mode === 'create' ? 'Subscribe MLS' : 'Update MLS';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>
        <Typography variant="h3">
          {modalTitle}
        </Typography>
      </Modal.Header>
      <Modal.Body className="py-8">
        <form id="mls-form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="space-y-6">
            <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
              <div className="flex items-center gap-4 mb-3">
                <IconPill icon={Building2} size="lg" />
                <h4 className="text-sm font-bold text-foreground">MLS Information</h4>
              </div>

              <hr className="dark:border-white/10" />
              <div className="grid grid-cols-1 gap-4 mt-5">
                <Form.Control>
                  <Form.Label>MLS Name</Form.Label>
                  <Input
                    {...register('name')}
                    error={errors.name?.message}
                    placeholder="Columbus Board of Realtors"
                    disabled={false}
                  />
                </Form.Control>

                <Form.Control>
                  <Form.Label>Board Code</Form.Label>
                  <Input
                    {...register('boardCode')}
                    error={errors.boardCode?.message}
                    placeholder="COLB"
                    disabled={mode === 'update'}
                  />
                </Form.Control>

                <Form.Control>
                  <Form.Label>Logo URL</Form.Label>
                  <Input
                    type="url"
                    {...register('logo')}
                    error={errors.logo?.message}
                    placeholder="https://example.com/logo.png"
                  />
                  <Form.HelperText>Provide the URL of the MLS logo image</Form.HelperText>
                </Form.Control>

                <Form.Control>
                  <Form.Label>Compliance Text</Form.Label>
                  <Controller
                    name="complianceText"
                    control={control}
                    render={({ field }) => (
                      <FormattedTextarea
                        value={field.value || ''}
                        onChange={field.onChange}
                        error={errors.complianceText?.message}
                        placeholder="© 2025 {Columbus Board of Realtors}. All rights reserved."
                      />
                    )}
                  />
                  <Form.HelperText>
                    Available autopopulate tags: {'{'}yyyy{'}'} (year), {'{'}mm/dd/yyyy hh:mm:ss
                    AM/PM{'}'} (full datetime), {'{'}mm/dd/yyyy{'}'} (date), {'{'}tenant_name{'}'}{' '}
                    (tenant name).
                  </Form.HelperText>
                </Form.Control>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15 mt-6">
              <div className="flex items-center gap-4 mb-3">
                <IconPill icon={Building2} size="lg" />
                <h4 className="text-sm font-bold text-foreground">Agent and Office IDs</h4>
              </div>

              <hr className="dark:border-white/10" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                <Form.Control>
                  <Form.Label>Agent IDs</Form.Label>
                  <div className="mt-2 border border-gray-300 dark:border-white/15 rounded-lg p-4 bg-white dark:bg-white/5">
                    <div className="flex flex-col gap-4">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Add agent IDs to associate with this MLS
                      </div>
                      <Input
                        placeholder="Type an agent ID and press Enter"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const value = e.currentTarget.value.trim();
                            if (value) {
                              // Check if agent ID already exists (case insensitive)
                              const exists = agentIds.some(
                                (id) => id.trim().toLowerCase() === value.toLowerCase()
                              );
                              if (!exists) {
                                setAgentIds([...agentIds, value.trim()]);
                                e.currentTarget.value = '';
                              }
                            }
                          }
                        }}
                      />
                      {agentIds.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {agentIds.map((id) => (
                            <div
                              key={id}
                              className="flex items-center gap-1 px-2 py-1 bg-foreground/10 text-foreground rounded-md"
                            >
                              <span className="text-sm">{id}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  setAgentIds(agentIds.filter((i) => i !== id));
                                }}
                                className="text-primary hover:text-primary/80"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </Form.Control>

                <Form.Control>
                  <Form.Label>Office IDs</Form.Label>
                  <div className="mt-2 border border-gray-300 dark:border-white/15 rounded-lg p-4 bg-white dark:bg-white/5">
                    <div className="flex flex-col gap-4">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Add office IDs to associate with this MLS
                      </div>
                      <Input
                        placeholder="Type an office ID and press Enter"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const value = e.currentTarget.value.trim();
                            if (value) {
                              // Check if office ID already exists (case insensitive)
                              const exists = officeIds.some(
                                (id) => id.trim().toLowerCase() === value.toLowerCase()
                              );
                              if (!exists) {
                                setOfficeIds([...officeIds, value.trim()]);
                                e.currentTarget.value = '';
                              }
                            }
                          }
                        }}
                      />
                      {officeIds.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {officeIds.map((id) => (
                            <div
                              key={id}
                              className="flex items-center gap-1 px-2 py-1 bg-foreground/10 text-foreground rounded-md"
                            >
                              <span className="text-sm">{id}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  setOfficeIds(officeIds.filter((i) => i !== id));
                                }}
                                className="text-primary hover:text-primary/80"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </Form.Control>
              </div>
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4 w-full">
          <div className="flex gap-2 w-full md:w-auto ml-auto justify-end">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form="mls-form" isLoading={isSubmitting}>
              {submitButtonText}
            </Button>
          </div>
        </div>
      </Modal.Footer>
      <LoadingDialog isOpen={!!loadingMessage} message={loadingMessage} />
    </Modal>
  );
};
